import React, { useEffect, useRef, useState } from 'react';

import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import styled from 'styled-components';

import * as actions from 'actions';
import { Input } from 'Components-redesign/Fields/TextInput/styles';
import { OnboardingBar } from 'Components-redesign/OnboardingBar';
import { useDispatch, useSelector } from 'Hooks';
import { Language } from 'Hooks/useI18n';
import { filterAllCompanies } from 'utils/filterAllCompanies';
import { getCountryFlag } from 'utils/general';

import { companyQuery } from './__queries__';
import {
  CompanyName,
  CompanyWrapper,
  DropWrapper,
  Info,
  LanguageDropdown,
  LanguageWrapper,
  LeftWrapper,
  Logo,
  RightWrapper,
  UserName,
  Wrapper,
} from './styles';
import ImageDrop from '../Fields/ImageSelect/ImageDrop';

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 200px;
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background: white;
  border: 1px solid var(--colorBlueGray300);
  border-top: none;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  border-radius: 0 0 4px 4px;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: var(--colorBlueGray900);
  border-bottom: 1px solid var(--colorBlueGray100);

  &:hover {
    background-color: var(--colorBlueGray100);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResults = styled.div`
  padding: 12px;
  text-align: center;
  color: var(--colorBlueGray600);
  font-size: 14px;
`;

const CompanyInfoWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: 680px) {
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
  }
`;

interface Props {
  companyName: string;
  userName: string;
  userRole: string;
  defaultLanguage: string;
}

const languageOptions = [
  { value: 'en', title: 'EN' },
  { value: 'se', title: 'SE' },
  { value: 'es', title: 'ES' },
];

const COMPANY_QUERY = gql`
  query companyQuery($searchTerm: String!) {
    companies: allCompanies(
      filter: {
        active: true
        OR: [{ name_contains: $searchTerm }, { venues_some: { name_contains: $searchTerm } }]
      }
    ) {
      id
      name
      languages
      currencies
      defaultCurrency
      country
      defaultLanguage
      visibleVMCategories
      hasAppOrders
    }
  }
`;
const HeaderBar: React.FC<Props> = ({ companyName, userName, userRole, defaultLanguage }) => {
  const dispatch = useDispatch();
  const currentLanguage = localStorage.getItem('BOLanguage') || defaultLanguage;
  const [selectedLanguage, setSelectedLanguage] = useState<Language | string>(currentLanguage);
  const [searchCompanyName, setSearchCompanyName] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const superAdminUserType = useSelector((state) => state.superAdminUserType);

  // Debounce search term for GraphQL query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchCompanyName);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchCompanyName]);

  // Fetch companies to enable company select.
  const { data, loading } = useQuery<companyQuery>(COMPANY_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !debouncedSearch.length,
    variables: { searchTerm: debouncedSearch },
  });

  const companies = filterAllCompanies(data?.companies ?? [], superAdminUserType);

  // Sort companies by name.
  const sortedAndFiltered = [...companies].sort((a, b) => (a.name > b.name ? 1 : -1));

  useEffect(() => {
    dispatch(actions.setBOLanguage(selectedLanguage));
    localStorage.setItem('BOLanguage', selectedLanguage);
  }, [selectedLanguage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Open dropdown when there are results
  useEffect(() => {
    if (searchCompanyName && sortedAndFiltered.length > 0) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [searchCompanyName, sortedAndFiltered.length]);

  const handleCompanySelect = (company: { id: string; name: string }) => {
    dispatch(actions.setCompany(company.id));
    setSearchCompanyName('');
    setIsDropdownOpen(false);
    window.location.reload();
  };

  return (
    <Wrapper>
      <LeftWrapper>
        <Logo to="/" />
      </LeftWrapper>

      <OnboardingBar />

      <LanguageWrapper isSuperAdmin={userRole === 'SUPER_ADMIN'}>
        <LanguageDropdown
          options={languageOptions}
          value={selectedLanguage}
          onChange={(event) => setSelectedLanguage(event.target.value)}
        />
      </LanguageWrapper>
      <RightWrapper>
        <DropWrapper>
          <ImageDrop />
        </DropWrapper>

        <CompanyInfoWrapper>
          <Info isSuperAdmin={userRole === 'SUPER_ADMIN'}>
            <CompanyName>{companyName}</CompanyName>
            <UserName>{userName}</UserName>
          </Info>
          {userRole === 'SUPER_ADMIN' && (
            <CompanyWrapper>
              <DropdownContainer ref={dropdownRef}>
                <Input
                  placeholder="Search company..."
                  type="text"
                  value={searchCompanyName}
                  style={{
                    width: '100%',
                    flex: '1',
                  }}
                  onChange={(e) => setSearchCompanyName(e.target.value)}
                  onFocus={() => searchCompanyName && setIsDropdownOpen(true)}
                />
                <DropdownList isOpen={isDropdownOpen}>
                  {loading && debouncedSearch ? (
                    <NoResults>Loading...</NoResults>
                  ) : sortedAndFiltered.length > 0 ? (
                    sortedAndFiltered.map((company) => (
                      <DropdownItem key={company.id} onClick={() => handleCompanySelect(company)}>
                        {getCountryFlag(company.country || company.defaultLanguage)} {company.name}
                      </DropdownItem>
                    ))
                  ) : (
                    searchCompanyName &&
                    debouncedSearch && <NoResults>No companies found</NoResults>
                  )}
                </DropdownList>
              </DropdownContainer>
            </CompanyWrapper>
          )}
        </CompanyInfoWrapper>
      </RightWrapper>
    </Wrapper>
  );
};

export default HeaderBar;
